import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, ChevronRight, Clipboard, Download } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { sefazCommunicationLogService } from '@services/sefazCommunicationLogService';
import { SefazCommunicationLog, getSefazServiceTypeLabel } from '@/types/sefazCommunicationLog';
import { getInvoiceModelLabel } from '@/types/invoice';
import { formatCnpj } from '@/types/company';

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
  } catch {
    return iso;
  }
};

const formatExecutionTime = (ms?: number | null) => {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

const formatXml = (xml: string): string => {
  try {
    const INDENT = '  ';
    let depth = 0;
    return xml
      .replace(/>\s*</g, '>\n<')
      .split('\n')
      .map((line) => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        if (trimmed.startsWith('</')) depth = Math.max(0, depth - 1);
        const padded = INDENT.repeat(depth) + trimmed;
        if (
          trimmed.startsWith('<') &&
          !trimmed.startsWith('</') &&
          !trimmed.startsWith('<?') &&
          !trimmed.startsWith('<!') &&
          !trimmed.endsWith('/>') &&
          !trimmed.includes('</')
        ) {
          depth++;
        }
        return padded;
      })
      .filter((l) => l !== '')
      .join('\n');
  } catch {
    return xml;
  }
};

const XmlAccordion: React.FC<{ title: string; filename: string; xml?: string | null }> = ({
  title,
  filename,
  xml,
}) => {
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasXml = !!xml;
  const formatted = hasXml ? formatXml(xml!) : '';

  const handleCopy = () => {
    if (!xml) return;
    navigator.clipboard.writeText(formatted).then(() => {
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    if (!xml) return;
    const blob = new Blob([formatted], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="flex items-center gap-2 text-slate-700 font-medium"
            onClick={() => setOpen((v) => !v)}
            disabled={!hasXml}
          >
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            {title}
            {!hasXml && <span className="text-xs text-slate-400">(vazio)</span>}
          </button>
          {hasXml && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded px-2 py-1 bg-white hover:bg-slate-50 transition-colors"
                title="Copiar XML"
              >
                {copied ? <Check size={13} className="text-emerald-600" /> : <Clipboard size={13} />}
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded px-2 py-1 bg-white hover:bg-slate-50 transition-colors"
                title="Baixar XML"
              >
                <Download size={13} />
                Baixar XML
              </button>
            </div>
          )}
        </div>
        {open && hasXml && (
          <pre className="mt-3 max-h-[32rem] overflow-auto bg-slate-900 text-slate-100 text-xs leading-relaxed p-4 rounded font-mono whitespace-pre">
            {formatted}
          </pre>
        )}
      </CardContent>
    </Card>
  );
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
    <p className="text-slate-800 text-sm mt-0.5 break-all">{value ?? '—'}</p>
  </div>
);

export const SefazCommunicationLogDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [item, setItem] = useState<SefazCommunicationLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    sefazCommunicationLogService
      .getById(Number(id))
      .then(setItem)
      .catch((err) => setError(err instanceof Error ? err.message : 'Falha ao carregar log SEFAZ'))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <PermissionGuard
      permission="sefaz_communication_log.read"
      fallback={
        <Card>
          <CardContent>
            <p className="text-slate-600">Você não tem permissão para visualizar logs da SEFAZ.</p>
          </CardContent>
        </Card>
      }
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate('/settings/fiscal/sefaz-communication-logs')}
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">Log SEFAZ #{id}</h1>
            <p className="text-slate-600">Detalhe da comunicação com a SEFAZ.</p>
          </div>
        </div>

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent>
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card>
            <CardContent>
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-6 animate-pulse rounded bg-slate-200" />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : item ? (
          <>
            <Card>
              <CardHeader title="Resumo" />
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Field label="Empresa" value={item.company_name || `#${item.company_id}`} />
                  <Field
                    label="CNPJ"
                    value={item.company_document ? formatCnpj(item.company_document) : '—'}
                  />
                  <Field label="Tipo" value={getSefazServiceTypeLabel(item.service_type)} />
                  <Field label="Modelo" value={getInvoiceModelLabel(item.model)} />
                  <Field label="Série" value={item.serie ?? '—'} />
                  <Field label="Nº Nota" value={item.invoice_number ?? '—'} />
                  <Field
                    label="Chave de acesso"
                    value={
                      item.access_key ? (
                        <span className="font-mono text-xs">{item.access_key}</span>
                      ) : (
                        '—'
                      )
                    }
                  />
                  <Field
                    label="cStat"
                    value={
                      <span className="font-mono">
                        {item.sefaz_status_code ?? '—'}
                      </span>
                    }
                  />
                  <Field label="Tempo de execução" value={formatExecutionTime(item.execution_time_ms)} />
                  <Field label="Criado em" value={formatDate(item.created_at)} />
                  <Field
                    label="xMotivo"
                    value={item.sefaz_status_description || '—'}
                  />
                </div>
              </CardContent>
            </Card>

            <XmlAccordion
              title="XML Request"
              filename={`sefaz-request-${id}.xml`}
              xml={item.request_xml}
            />
            <XmlAccordion
              title="XML Response"
              filename={`sefaz-response-${id}.xml`}
              xml={item.response_xml}
            />
          </>
        ) : null}
      </div>
    </PermissionGuard>
  );
};
